'use client';

import { useTranslations } from 'next-intl';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { useOutcomeLabel } from '@/components/match/matchOutcomeLabel';
import { MatchResultCard, type MatchResultSide } from '@/components/match/MatchResultCard';
import { formatTime } from '@/lib/utils';
import { useHydratedTimeZone } from '@/hooks/useHydratedTimeZone';
import { isWinnerlessRow } from '@/draw/core/matchOutcome';
import type { PublicMatch } from '@/app/[locale]/event/[eventId]/[slug]/(tabs)/schedule/libs/scheduleStatus';
import type { WatchRow } from '@/app/[locale]/event/[eventId]/libs/watchMatches';
import {
  cardState,
  matchCardStatus,
  sideScoresLabel,
  type MatchPhase,
} from '@/app/[locale]/event/[eventId]/libs/matchCardStatus';
import { rosterInline } from '@/app/[locale]/event/[eventId]/libs/teamLabels';
import { displayMatchCode } from '@/app/[locale]/event/[eventId]/libs/matchCode';
import { NameText } from '../../components/NameText';

/** Which side won a finished match: the recorded winner, else the games leader. */
function winnerIndex(match: PublicMatch): 0 | 1 | null {
  if (match.status !== 'completed') return null;
  if (match.winnerId && match.playerIds) {
    const i = match.playerIds.findIndex((id) => id === match.winnerId);
    if (i === 0 || i === 1) return i;
  }
  if (!match.score || match.score.p1 === match.score.p2) return null;
  return match.score.p1 > match.score.p2 ? 0 : 1;
}

/** One watch row: maps a schedule match onto the shared match card, with a play action when it has video. */
function Row({
  row,
  selected,
  onSelect,
  locale,
}: {
  row: WatchRow<PublicMatch>;
  selected: boolean;
  onSelect: (row: WatchRow<PublicMatch>) => void;
  locale: string;
}) {
  const tMatches = useTranslations('Event.matches');
  const tMatchesCommon = useTranslations('Matches');
  const tWatch = useTranslations('EventWatch');
  const outcomeLabel = useOutcomeLabel();
  const timeZone = useHydratedTimeZone();
  const { match } = row;
  const playable = row.kind !== 'none';
  const winner = winnerIndex(match);
  const names = [match.players[0] ?? '', match.players[1] ?? ''];
  const caption = names.join(` ${tWatch('vs')} `);
  // The time tells a rematch apart; it formats after mount, like the visible time.
  const label = `${caption}, ${formatTime(match.scheduledAt, locale, timeZone)}`;

  const winnerless = isWinnerlessRow({ outcome: match.outcome, is_walkover: match.isWalkover });
  // Winnerless first, as on Matches: a walkover has no `match` row, so its status reads upcoming.
  const phase: MatchPhase =
    match.status === 'live' ? 'live' : match.status === 'upcoming' ? 'upcoming' : 'finished';
  const state = cardState(winnerless, phase);
  const outcomeWord = state === 'winnerless' ? outcomeLabel(match.outcome, match.isWalkover) : null;
  const { statusWord, status } = matchCardStatus({
    state,
    outcomeWord,
    retired: match.outcome === 'retired',
    words: {
      live: tMatches('statusLive'),
      upcoming: tMatches('statusUpcoming'),
      finished: tMatches('statusFinished'),
      retiredShort: tMatchesCommon('retiredShort'),
    },
  });

  // Upcoming and winnerless outcomes have no played-score presentation (as on Matches).
  const hasScores = state !== 'upcoming' && state !== 'winnerless';
  const games = hasScores ? (match.games ?? []) : [];
  const mapSide = (p: 0 | 1): MatchResultSide => {
    const name = names[p]!;
    const score = hasScores ? match.score : null;
    const side: MatchResultSide = {
      name:
        name === '' ? (
          <span className="text-muted">{tMatchesCommon('tbd')}</span>
        ) : (
          <NameText name={name} />
        ),
      roster: rosterInline(match.rosters?.[p]) ?? undefined,
      won: winner === p,
      lost: winner !== null && winner !== p,
      gamesWon: score ? (p === 0 ? score.p1 : score.p2) : null,
      games: games.map((g) => {
        const mine = p === 0 ? g.p1 : g.p2;
        const theirs = p === 0 ? g.p2 : g.p1;
        return { score: mine, won: !g.partial && mine > theirs };
      }),
    };
    return { ...side, scoresLabel: sideScoresLabel((v) => tMatches('sideScores', v), side) };
  };

  return (
    <MatchResultCard
      id={match.id}
      state={state}
      statusWord={statusWord}
      winnerWord={tMatches('winner')}
      header={{
        code: displayMatchCode(match.code) ?? undefined,
        time: (
          <LocalDateTime
            iso={match.scheduledAt}
            locale={locale}
            format={formatTime}
            className="text-muted font-mono text-xs font-semibold tabular-nums"
          />
        ),
        division: match.division ?? undefined,
        status,
      }}
      sides={[mapSide(0), mapSide(1)]}
      action={
        playable
          ? { kind: 'play', onClick: () => onSelect(row), label, active: selected }
          : undefined
      }
    />
  );
}

/**
 * The court-day's matches, forwards, as cards. The list flows with the page; from `lg` it sits
 * beside the sticky player column, which sets nothing about its height.
 */
export function WatchMatchList({
  rows,
  scheduleFailed = false,
  selectedId,
  onSelect,
  dayLabel,
  courtName,
  locale,
}: {
  rows: WatchRow<PublicMatch>[];
  /** The schedule read failed; an empty list then means "unknown", not "none". */
  scheduleFailed?: boolean;
  selectedId: string | null;
  onSelect: (row: WatchRow<PublicMatch>) => void;
  dayLabel: string;
  courtName: string;
  locale: string;
}) {
  const t = useTranslations('EventWatch');
  return (
    <aside data-testid="watch-match-list" className="min-w-0 lg:col-start-2 lg:row-start-1">
      <div className="flex items-baseline justify-between gap-2 pb-2">
        <h3 className="text-foreground min-w-0 truncate text-sm font-semibold">
          <span suppressHydrationWarning>
            {t('dayOnCourt', { day: dayLabel, court: courtName })}
          </span>
        </h3>
        {!scheduleFailed && (
          <span className="text-muted shrink-0 text-xs">
            {t('matchCount', { count: rows.length })}
          </span>
        )}
      </div>
      {rows.length === 0 && (
        <p role={scheduleFailed ? 'alert' : undefined} className="text-muted px-1 py-2 text-sm">
          {scheduleFailed ? t('matchesFailed') : t('noMatches')}
        </p>
      )}
      <ul className="grid gap-2">
        {rows.map((row) => (
          <Row
            key={row.match.id}
            row={row}
            selected={row.match.id === selectedId}
            onSelect={onSelect}
            locale={locale}
          />
        ))}
      </ul>
    </aside>
  );
}
